import { useApolloClient, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorPopup } from '../helpers/dialogs';
import { paymentCheckoutQuery } from './operations';
import { clearMissedRenewal } from './premiumCache';
import { PremiumUser } from './premiumState';

export function usePaymentConfirmation(onConfirmed: (user: PremiumUser) => void) {
    const { t } = useTranslation('subscription');
    const client = useApolloClient();
    const [paymentId, setPaymentId] = useState<string>();
    const { data, error } = useQuery<{ paymentCheckout: { status: string; user?: PremiumUser | null } | null }, { paymentId: string }>(
        paymentCheckoutQuery,
        {
            variables: { paymentId: paymentId ?? '' },
            skip: !paymentId,
            fetchPolicy: 'network-only',
            pollInterval: 1500,
        },
    );

    useEffect(() => {
        if (!paymentId) return;
        if (data?.paymentCheckout?.user?.subscription) {
            clearMissedRenewal(client.cache);
            onConfirmed(data.paymentCheckout.user);
            setPaymentId(undefined);
        } else if (error || data?.paymentCheckout === null || ['failed', 'refunded'].includes(data?.paymentCheckout?.status ?? '')) {
            setPaymentId(undefined);
            showErrorPopup('Payment', t('paymentError'));
        }
    }, [client, data, error, paymentId, t, onConfirmed]);

    useEffect(() => {
        if (!paymentId) return;
        const timeout = window.setTimeout(() => {
            setPaymentId(undefined);
            showErrorPopup('Payment', t('paymentPending'));
        }, 60000);
        return () => window.clearTimeout(timeout);
    }, [paymentId, t]);

    return { confirmPayment: setPaymentId, confirming: Boolean(paymentId) };
}
